/**
 * Store Pinia des articles (composition API)
 */
import { defineStore } from "pinia";
import { ref } from "vue";
import ArticleService from "../services/ArticleService";
import { IArticle } from "../models/article";
import { IArticleForm } from "../models/forms/articleForm";
import { IArticleApiResponse } from "../models/api/articleApiResponse";
import { IArticleFilter } from "../models/filters/articleFilter";
import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import { IDataOperationResult } from "../models/api/dataOperationResult";
import DataOperationError from "../models/api/dataOperationError";
import { IParameter } from "../models/parameter";
import { useParameterStore } from "./parameter.store";
import signalRManager from "../helper/signalRManager";
import IPayloadStore from "../models/api/payloadStore";

export const useArticleStore = defineStore("articles", () => {
    // State
    const datas = ref<IArticle[]>([]);
    const current = ref<IArticle>({} as IArticle);
    const itemForm = ref<IArticleForm>({} as IArticleForm);
    const isLoading = ref<boolean>(false);

    // Actions - Hydration
    /**
     * Hydrate une liste d'articles en batch (optimisé pour la performance)
     * - Charge le parameterStore si nécessaire
     * - Crée une Map d'index pour des lookups O(1)
     */
    async function hydrateArticles(articles: IArticleApiResponse[]): Promise<IArticle[]> {
        if (articles.length === 0) return [];

        // 1. Récupérer le store dépendant
        const parameterStore = useParameterStore();

        // 2. Charger si nécessaire
        if (parameterStore.datas.length === 0) {
            await parameterStore.load({ page: 1, pageSize: 0 });
        }

        // 3. Créer une Map pour des lookups O(1)
        const parameterMap = new Map(parameterStore.datas.map(p => [p.id, p]));

        // 4. Hydrater tous les articles en une seule passe
        const models: IArticle[] = [];
        for (const article of articles) {
            const model = { ...article } as unknown as IArticle;

            // Lier la référence geneticType (lookup O(1) via Map)
            if (article.geneticTypeId) {
                model.geneticType = parameterMap.get(article.geneticTypeId) ?? model.geneticType;
            }

            models.push(model);
        }

        return models;
    }

    // Actions

    /**
     * Chargement des données dans le store
     */
    async function load(filter: IArticleFilter) {
        isLoading.value = true;
        const result = await ArticleService.getAll<IArticleFilter, IArticleApiResponse>(filter);
        isLoading.value = false;
        datas.value = [];

        if (!result.error) {
            datas.value = await hydrateArticles(result.data as IArticleApiResponse[]);
        }

        return result;
    }

    /**
     * Récupération d'un article par son identifiant
     */
    async function get(id: number) {
        const result = await ArticleService.getById<IArticleApiResponse>(id);

        if (!result.error) {
            const [hydratedArticle] = await hydrateArticles([result.data as IArticleApiResponse]);

            const index = datas.value.findIndex((a) => a.id == id);
            if (index >= 0)
                datas.value[index] = hydratedArticle;

            current.value = hydratedArticle;
            itemForm.value = toForm(hydratedArticle);
        }

        return result;
    }

    /**
     * Récupération de l'objet dans le store
     */
    async function searchStoreById(id: number): Promise<IArticle> {
        current.value = datas.value.find((objet) => objet.id == id)!;
        itemForm.value = toForm(current.value as IArticle);
        return current.value;
    }

    /**
     * Mise à jour ou création d'un objet
     */
    async function save(maj: boolean = true, isPatch: boolean = false) {
        let result = {} as ServiceOperationResult<IArticle | IDataOperationResult<IArticle>>;

        if (itemForm.value.id > 0) {
            result = await ArticleService.update(itemForm.value);

            if (!result.error) {
                const [hydratedArticle] = await hydrateArticles([result.data as IArticleApiResponse]);
                current.value = hydratedArticle;

                if (maj) {
                    const index = datas.value.findIndex((a) => a.id == itemForm.value.id);
                    if (index >= 0) {
                        datas.value[index] = current.value;
                    } else {
                        const error = new DataOperationError();
                        error.REQUEST_ERROR = "Item non trouvé dans le store";
                        return new ServiceOperationResult<IDataOperationResult<IArticle>>(true, { errors: error });
                    }
                }
            }
        } else {
            result = await ArticleService.create(itemForm.value);

            if (!result.error) {
                const [hydratedArticle] = await hydrateArticles([result.data as IArticleApiResponse]);
                current.value = hydratedArticle;
                if (datas.value.findIndex((a) => a.id == current.value?.id) == -1) {
                    datas.value.push(current.value);
                }
            }
        }

        return result;
    }

    /**
     * Delete de l'objet
     */
    async function deleteAction() {
        const result = await ArticleService.delete<IArticleApiResponse>(current.value.id);

        if (!result.error) {
            const index = datas.value.findIndex((a) => a.id == current.value.id);
            if (index >= 0) {
                datas.value.splice(index, 1);
            }

            current.value = newItem();
            itemForm.value = newForm();
        }

        return result;
    }

    /**
     * Transformation de l'objet en formulaire
     */
    function toForm(item: IArticle): IArticleForm {
        const form = {} as IArticleForm;
        form.id = item.id;
        form.code = item.code;
        form.libelle = item.libelle;
        form.isMaterial = item.isMaterial;
        form.geneticTypeId = item.geneticType ? item.geneticType.id : 0;
        itemForm.value = form;
        return form;
    }

    /**
     * Permet d'initialiser un objet vide pour le formulaire
     */
    function newForm(): IArticleForm {
        itemForm.value = {
            id: 0,
            code: "",
            libelle: "",
            isMaterial: false,
            geneticTypeId: 0
        } as IArticleForm;
        return itemForm.value;
    }

    /**
     * Permet d'initialiser un objet vide pour les referentiels
     */
    function newItem(): IArticle {
        current.value = {
            id: 0,
            code: "",
            libelle: "",
            isMaterial: false,
            geneticType: { id: 0 } as IParameter,
        } as IArticle;
        return current.value;
    }

    return {
        // State
        datas,
        current,
        itemForm,
        isLoading,
        // Actions
        hydrateArticles,
        load,
        get,
        searchStoreById,
        save,
        delete: deleteAction,
        toForm,
        newForm,
        newItem,
    };
});

signalRManager.connection.on("MajArticle", async (payload: IPayloadStore) => {
    const store = useArticleStore();
    switch (payload.action) {
        case "Create": {
            if (store.datas.findIndex((a) => a.id == payload.data?.id) == -1) {
                const [hydrated] = await store.hydrateArticles([payload.data as IArticleApiResponse]);
                store.datas.push(hydrated);
            }
            break;
        }
        case "Update": {
            const idx = store.datas.findIndex((a) => a.id == payload.data?.id);
            if (idx > -1) {
                const [hydrated] = await store.hydrateArticles([payload.data as IArticleApiResponse]);
                store.datas.splice(idx, 1, hydrated);
            }
            break;
        }
        case "Delete": {
            const idx = store.datas.findIndex((a) => a.id == payload.id);
            if (idx > -1) store.datas.splice(idx, 1);
            break;
        }
    }
});
